/**
 * Badge.
 *
 * Generic tinted pill. For status indicators, prefer the more opinionated
 * `StatusBadge` component which encodes role semantics. Use Badge when the
 * tag is decorative or domain-specific (e.g. "Variant A").
 */
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'attention';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-subtle text-secondary',
  success: 'bg-status-success-bg text-status-success',
  warning: 'bg-status-warning-bg text-status-warning',
  danger: 'bg-status-danger-bg text-status-danger',
  info: 'bg-status-info-bg text-status-info',
  // Patch P1 — accent + attention, see styles.css for rationale.
  accent: 'bg-status-accent-bg text-status-accent',
  attention: 'bg-status-attention-bg text-status-attention',
};

export function Badge({ tone = 'neutral', className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
